import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Lock, Bank, Copy, SignOut, GraduationCap, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Full-screen overlay shown when the user's subscription/trial has expired
 * or their account is blocked. Triggers:
 *   - On mount, if `user.plan.is_active === false`
 *   - When any API call returns 402 (via CustomEvent "tutorpro:subscriptionInactive")
 */
export default function SubscriptionGate() {
  const { user, logout, refreshUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState(null);
  const [billing, setBilling] = useState(null);

  // Show overlay based on user plan status
  useEffect(() => {
    if (user && user.plan && user.plan.is_active === false) {
      setVisible(true);
      setDetail({
        status: user.plan.status,
        is_blocked: user.plan.is_blocked,
        message: user.plan.is_blocked
          ? "Hesabınız engellenmiş. Destek ile iletişime geçin."
          : "Aboneliğiniz sona erdi. Devam etmek için yıllık ödemenizi yapın.",
      });
    } else if (user && user.plan && user.plan.is_active) {
      setVisible(false);
    }
  }, [user]);

  // Listen for any 402 from axios interceptor
  useEffect(() => {
    const handler = (e) => {
      setDetail(e.detail || {});
      setVisible(true);
    };
    window.addEventListener("tutorpro:subscriptionInactive", handler);
    return () => window.removeEventListener("tutorpro:subscriptionInactive", handler);
  }, []);

  // Fetch billing info when overlay opens
  useEffect(() => {
    if (!visible) return;
    api.get("/billing/info").then((r) => setBilling(r.data)).catch(() => {});
  }, [visible]);

  async function copyIban() {
    try {
      await navigator.clipboard.writeText(billing?.bank_iban || "");
      toast.success("IBAN kopyalandı.");
    } catch {
      toast.error("Kopyalanamadı.");
    }
  }

  async function refresh() {
    const u = await refreshUser();
    if (u && u.plan && u.plan.is_active) {
      setVisible(false);
      toast.success("Aboneliğiniz aktifleştirildi!");
    } else {
      toast.info("Henüz ödeme görünmüyor. Havale yaptıysanız admin'i bekleyin.");
    }
  }

  if (!visible) return null;

  const isBlocked = detail?.is_blocked || detail?.status === "blocked";
  const isTrialExpired = detail?.status === "trial_expired";
  const price = billing?.annual_price;
  const currency = billing?.currency || "TRY";
  const hasBankInfo = billing?.bank_name || billing?.bank_iban;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="subscription-gate"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full my-8 overflow-hidden animate-fade-in">
        <div className={`px-6 py-5 flex items-center gap-3 border-b border-border ${isBlocked ? "bg-[#FDECEA]" : "bg-[#FFF3E0]"}`}>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${isBlocked ? "bg-[#D32F2F] text-white" : "bg-[#D97757] text-white"}`}>
            {isBlocked ? <WarningCircle size={22} weight="fill" /> : <Lock size={22} weight="duotone" />}
          </div>
          <div className="min-w-0">
            <div className="font-display text-xl font-bold leading-tight">
              {isBlocked ? "Hesap engellendi" : isTrialExpired ? "Deneme süreniz doldu" : "Aboneliğiniz sona erdi"}
            </div>
            <div className="text-sm text-muted-foreground mt-0.5 truncate">
              {user?.email}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {isBlocked ? (
            <p className="text-sm text-foreground">
              Hesabınız yönetici tarafından engellenmiştir. Sorularınız için destek ile iletişime geçebilirsiniz.
            </p>
          ) : (
            <>
              <p className="text-sm text-foreground leading-relaxed">
                EduFin'i kullanmaya devam etmek için yıllık aboneliği yenilemeniz gerekiyor.
                Aşağıdaki banka hesabına havale yaparak ödemenizi tamamlayın; ödemeniz onaylandıktan sonra hesabınız otomatik olarak aktif edilir.
              </p>

              <div className="rounded-lg border border-border bg-[#FAF9F6] p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Yıllık abonelik</div>
                    <div className="font-display text-3xl font-bold text-[#3A5A40]" data-testid="gate-price">
                      {price != null ? formatCurrency(price, currency) : "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[#3A5A40]">
                    <GraduationCap size={28} weight="duotone" />
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Yıllık (12 ay) kesintisiz kullanım. Ödeme sonrası aynı gün içinde aktivasyon.
                </div>
              </div>

              {hasBankInfo ? (
                <div className="rounded-lg border border-border divide-y divide-border overflow-hidden" data-testid="gate-bank-info">
                  <div className="px-4 py-2.5 bg-muted/40 flex items-center gap-2 text-sm font-semibold">
                    <Bank size={16} weight="duotone" className="text-[#3A5A40]" /> Havale / EFT Bilgileri
                  </div>
                  {billing?.bank_name && (
                    <Row label="Banka" value={billing.bank_name} />
                  )}
                  {billing?.bank_account_holder && (
                    <Row label="Hesap Sahibi" value={billing.bank_account_holder} />
                  )}
                  {billing?.bank_iban && (
                    <Row
                      label="IBAN"
                      value={billing.bank_iban}
                      mono
                      action={
                        <Button size="sm" variant="outline" onClick={copyIban} className="h-7 px-2" data-testid="gate-copy-iban">
                          <Copy size={12} className="mr-1" /> Kopyala
                        </Button>
                      }
                    />
                  )}
                  {billing?.payment_note && (
                    <div className="px-4 py-3 text-xs text-muted-foreground bg-[#FFF3E0]/40">
                      <b className="text-foreground">Not:</b> {billing.payment_note}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  Ödeme yönlendirmesi için yöneticiniz henüz banka bilgilerini tanımlamamış. Lütfen destek ile iletişime geçin.
                </div>
              )}
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            {!isBlocked && (
              <Button
                onClick={refresh}
                className="bg-[#3A5A40] hover:bg-[#2E4833] text-white flex-1"
                data-testid="gate-refresh-button"
              >
                Ödemeyi yaptım · Kontrol et
              </Button>
            )}
            <Button
              variant="outline"
              onClick={async () => { await logout(); window.location.href = "/giris"; }}
              data-testid="gate-logout-button"
            >
              <SignOut size={14} className="mr-1" /> Çıkış yap
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, action }) {
  return (
    <div className="px-4 py-2.5 flex items-center gap-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground w-28 flex-shrink-0">{label}</div>
      <div className={`flex-1 min-w-0 break-all ${mono ? "font-mono text-sm" : "text-sm"}`}>{value}</div>
      {action}
    </div>
  );
}
